import "@app/App.css";
import CvsUploader from "@comps/ui/CvsUploader";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { importCsvLines } from "@utils/data";
import { toastError, toastSuccess } from "@utils/toasts";
import { TW_ObjectModelViewHeaderButton, TW_ObjectModelViewHeaderSmallerIcon } from "@utils/twClasses";
import { Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";

function ImportButton() {

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
  const selectedWorkspace = useModelStore((state) => state.selectedWorkspace);
  const { t } = useTranslation();
  const fetchData = useDataStore((state) => state.fetchData);
  const fields = selectedObjectInfos?.fields || null;
  const loadingData = useDataStore((state) => state.loadingData);
  const loadData = useDataStore((state) => state.loadData);
  const stopLoadingData = useDataStore((state) => state.stopLoadingData);
  const rowActionsEnabled = useDataStore(state => state.rowActionsEnabled);

  const handleImport = async (columns: string[], rows: any[][]) => {
    const workspaceKey = selectedWorkspace?._key;
    const objectKey = selectedObjectInfos?.object._key;
    if (!workspaceKey || !objectKey) {
      console.error("Selected workspace or object key not found");
      toastError(t("record.csv.import.error"));
      return;
    }

    loadData();
    try {
      const ok = await importCsvLines(workspaceKey, objectKey, columns, rows, t);
      if (ok) {
        toastSuccess(translate(t, "record.csv.import.success.1", [rows.length]));
        // Release the dialog first: the grid overlay is driven by loadingData
        // and stays up until the refreshed rows are in.
        setIsDialogOpen(false);
        await fetchData();
      }
    } finally {
      // Always release the spinner, whatever failed above.
      stopLoadingData();
    }
  }

  const isDisabled = (!fields || fields.length === 0) || !rowActionsEnabled;
  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger className={TW_ObjectModelViewHeaderButton + (isDisabled ? " opacity-50 cursor-not-allowed" : " opacity-100 cursor-default")} disabled={isDisabled}>
        <TooltipProvider>
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <Upload className={TW_ObjectModelViewHeaderSmallerIcon} />
            </TooltipTrigger>
            <TooltipContent>
              {t('object.header.button.import.tooltip')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </DialogTrigger>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("record.csv.import.title")}</DialogTitle>
          <DialogDescription hidden={true}>{t("record.csv.import.description")}</DialogDescription>
        </DialogHeader>
        <CvsUploader onImport={handleImport} loading={loadingData} />
      </DialogContent>
    </Dialog>
  )
}

export default ImportButton;